import moment from 'moment';
import { I18n } from 'react-redux-i18n';
import { REDUCER_NAME as COMMON_REDUCER_NAME } from '../../../../component/app/commonReducer';
//import { addSubscriber } from '../../../../component/app/realtimeSubscriberReducer';
import store from '../../../../component/app/store';
import {
  formatPrice,
  formatTextFloat,
  formatValueBillion,
} from '../../../../component/helpers/Text';
import {
  BUSDCode,
  getBUSDFromApi,
} from '../../../../component/technical/charting/trading-view/indicators/BUSD';
import {
  ForeignTradingCode,
  getForeignTradingFromApi,
} from '../../../../component/technical/charting/trading-view/indicators/ForeignTrading';
import chartingService from '../Charting';
import { icbTickerToCode, SYMBOL_TYPES } from './Const';
import { getFromToParams } from './helpers';

let symbolOrganCodeType = '';

const mapResolution = {
  1: 'EachMinute',
  5: 'EachFiveMinutes',
  15: 'EachFifteenMinutes',
  30: 'EachThirtyMinutes',
  60: 'EachOneHour',
  120: 'EachTwoHours',
  240: 'EachFourHours',
  D: 'Daily',
  W: 'Weekly',
  M: 'Monthly',
  '3M': 'Quarterly',
  '12M': 'Yearly',
  '1D': 'Daily',
  '1W': 'Weekly',
  '1M': 'Monthly',
};

const mapTypeToParam = {
  [SYMBOL_TYPES.STOCK]: 'Stock',
  [SYMBOL_TYPES.INDICES]: 'Index',
  [SYMBOL_TYPES.FUTURES]: 'Derivative',
  [SYMBOL_TYPES.ECONOMY]: 'Economy',
  [SYMBOL_TYPES.COVERED_WARRANT]: 'CW',
  [SYMBOL_TYPES.SECTOR]: 'Sector',
};

const getLang = () => store.getState().i18n.locale;

const getUser = () => store.getState().userInfo;

const getIssueMethodKey = () => {
  const mapLang = {
    en: 'en_IssueMethodName',
    vi: 'issueMethodName',
  };
  return mapLang[getLang()];
};

const getInternalTransactionName = () => {
  const mapLang = {
    en: 'en_FullName',
    vi: 'fullName',
  };

  return mapLang[getLang()];
};

export const setSymbolOrganCodeType = type => (symbolOrganCodeType = type);

const formatTime = time => {
  if (!time) {
    return '--';
  }
  const date = moment(time).toDate();
  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
};

const formatPercent = percent => {
  return percent % 1 === 0 ? percent : formatTextFloat(percent);
};

const timeScaleMarkTypes = {
  Earning: {
    color: 'brown',
    label: 'E',
    tooltips: [
      {
        i18nKey: 'revenue',
        getValue: item => formatTextFloat(formatValueBillion(item.revenue)),
      },
      {
        i18nKey: 'profit',
        getValue: item => formatTextFloat(formatValueBillion(item.profit)),
      },
      {
        i18nKey: 'periodEnding',
        getValue: item =>
          `Q${item.lengthReport || '--'} - ${item.yearReport || '--'}`,
      },
    ],
  },
  Dividend: {
    color: 'yellow',
    label: 'D',
    tooltips: [
      {
        i18nKey: 'dividends',
        getValue: item => formatTextFloat(item.valuePershare, 0),
      },
      {
        i18nKey: 'exRatio',
        getValue: item => formatPercent(item.exerciseRate * 100),
      },
      {
        i18nKey: 'exrightDate',
        getValue: item => {
          return formatTime(item.exrightDate);
        },
      },
    ],
  },
  ShareIssuance: {
    color: 'blue',
    label: 'I',
    tooltips: [
      {
        i18nKey: 'issueMethod',
        getValue: item => item[getIssueMethodKey()] || '--',
      },
      {
        i18nKey: 'volume',
        getValue: item => formatTextFloat(item.issueVolumn, 0),
      },
      {
        i18nKey: 'exRatio',
        getValue: item => formatPercent(item.exerciseRatio * 100),
      },
      {
        i18nKey: 'exIssueDate',
        getValue: item => formatTime(item.exrightDate),
      },
    ],
  },
  ShareIssuancePlan: {
    color: 'blue',
    label: 'I',
    tooltips: [
      {
        i18nKey: 'issueMethod',
        getValue: item => item[getIssueMethodKey()],
      },
      {
        i18nKey: 'planVolume',
        getValue: item => formatTextFloat(item.planVolumn, 0),
      },
      {
        i18nKey: 'exRatio',
        getValue: item => formatPercent(item.exerciseRatio * 100),
      },
    ],
  },
};

const isRegisterType = item => item.tradeStatusCode === 'R';
const getMarkValue = item => {
  return isRegisterType(item)
    ? Math.abs(item.shareRegister)
    : Math.abs(item.shareAcquire);
};

const markTypes = {
  InternalTransaction: {
    S: {
      color: 'red',
      label: 'S',
      i18nKey: 'sell',
      getValue: item => formatTextFloat(getMarkValue(item), 0),
    },
    B: {
      color: 'green',
      label: 'B',
      i18nKey: 'buy',
      getValue: item => formatTextFloat(getMarkValue(item), 0),
    },
  },
};

const getTimescaleTooltip = (eventItem, item) => {
  return eventItem.tooltips.map(tooltip =>
    I18n.t(`charting.${tooltip.i18nKey}`, { value: tooltip.getValue(item) }),
  );
};

const getUnixTime = date => {
  const dateTime = moment(date).toDate();
  const utcDate = new Date(
    Date.UTC(
      dateTime.getFullYear(),
      dateTime.getMonth(),
      dateTime.getDate(),
      dateTime.getHours(),
      dateTime.getMinutes(),
      dateTime.getSeconds(),
      dateTime.getMilliseconds(),
    ),
  );
  return Math.round(utcDate.getTime() / 1000);
};

const formatTimescaleMarks = (result, item) => {
  const timeString =
    item.type === 'Dividend' ? item.exrightDate : item.publicDate;
  if (!timeString) {
    return result;
  }
  const obj = {
    time: getUnixTime(timeString),
    id: item.id,
    labelFontColor: '#ffffff',
    minSize: 10,
  };
  if (!timeScaleMarkTypes[item.type]) {
    return result;
  }
  let type = item.type;
  if (type === 'ShareIssuance' && item.issueStatusCode === 'P') {
    type = 'ShareIssuancePlan';
  }
  return result.concat({
    ...obj,
    color: timeScaleMarkTypes[type].color,
    label: timeScaleMarkTypes[type].label,
    tooltip: [''].concat(getTimescaleTooltip(timeScaleMarkTypes[type], item)),
  });
};

const getMarkTooltip = (item, mark) => {
  const user = getUser();

  if (item.tradeStatusCode === 'D' && item.shareAcquire === 0) {
    return `${item[getInternalTransactionName()]} - ${I18n.t(
      `charting.${mark.i18nKey}NotHappen`,
      {
        value: formatTextFloat(item.shareRegister, 0),
      },
    )} - ${I18n.t(`charting.${mark.i18nKey}NotHappenStatus`)}`;
  }

  const i18nKey = isRegisterType(item)
    ? `charting.${mark.i18nKey}Register`
    : `charting.${mark.i18nKey}`;

  return `${item[getInternalTransactionName()] || '--'} - ${I18n.t(i18nKey, {
    value: mark.getValue(item),
  })}`;
};

const formatMarks = (result, item) => {
  const obj = {
    time: getUnixTime(item.publicDate),
    id: item.id,
    minSize: 5,
  };
  if (!markTypes[item.type] || !markTypes[item.type][item.actionTypeCode]) {
    return result;
  }
  const mark = markTypes[item.type][item.actionTypeCode];
  return result.concat({
    ...obj,
    labelFontColor: mark.color,
    color: {
      border: mark.color,
      background: 'transparent',
    },
    label: mark.label,
    text: getMarkTooltip(item, mark),
  });
};

const getEventParam = (symbolInfo, from, to) => ({
  OrganCode: symbolInfo.code,
  From: new Date(from * 1000).toISOString(),
  To: new Date(to * 1000).toISOString(),
});

export const getPrice = (symbolInfo, value) => {
  if (
    symbolInfo.type === SYMBOL_TYPES.STOCK ||
    symbolInfo.type === SYMBOL_TYPES.COVERED_WARRANT
  ) {
    return formatPrice(value);
  }

  return value;
};

export const getBarTime = (time, resolution) => {
  if (resolution.includes('D')) {
    const date = moment(time).toDate();

    return Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours(),
      date.getMinutes(),
    );
  }

  return moment(time)
    .startOf('minute')
    .toDate()
    .getTime();
};

const getBarParams = (symbolInfo, resolution, from, to) => {
  const dateRange = getFromToParams({ symbolInfo, resolution, from, to });
  if (!dateRange) return null;
  return {
    Code: symbolInfo.code,
    Frequency: mapResolution[resolution],
    // From: new Date(from * 1000).toISOString(),
    // To: new Date(to * 1000).toISOString(),
    From: dateRange.From.toISOString(),
    To: dateRange.To.toISOString(),
    Type: mapTypeToParam[symbolInfo.type],
  };
};

const getIndicatorParams = (symbolInfo, resolution, from, to) => {
  const [_, indicatorCode, ticker] = symbolInfo.name.split(':');
  const organization = store.getState()[COMMON_REDUCER_NAME]
    .listOrganizationByTicker[ticker];
  const sectorCode = icbTickerToCode[ticker];
  const dateRange = getFromToParams({ symbolInfo, resolution, from, to });
  if (!dateRange) return null;
  return {
    OganCode: organization
      ? organization.organCode
      : sectorCode
      ? sectorCode
      : ticker,
    Code: indicatorCode,
    Frequency: mapResolution[resolution],
    // From: new Date(from * 1000).toISOString(),
    // To: new Date(to * 1000).toISOString(),
    From: dateRange.From.toISOString(),
    To: dateRange.To.toISOString(),
    Type: mapTypeToParam[symbolOrganCodeType],
  };
};

export const isIndicator = symbolInfo => {
  return symbolInfo.name.includes('INDICATOR');
};

export const formatBar = (resolution, isFormatPrice = false) => item => ({
  time: getBarTime(item.tradingDate, resolution),
  low:
    item.lowestPrice && isFormatPrice
      ? formatPrice(item.lowestPrice)
      : item.lowestPrice,
  high:
    item.highestPrice && isFormatPrice
      ? formatPrice(item.highestPrice)
      : item.highestPrice,
  open:
    item.openPrice && isFormatPrice
      ? formatPrice(item.openPrice)
      : item.openPrice,
  close:
    item.closePrice && isFormatPrice
      ? formatPrice(item.closePrice)
      : item.closePrice,
  volume: item.totalMatchVolume,
});

const isValidIndicatorInterval = (symbolInfo, resolution, interval) => {
  const [_, indicatorName] = symbolInfo.name.split(':');
  // if (isQuarterlyData(indicatorName)) {
  //   return resolution === 'M';
  // }

  return ['D', '1D', 'W', '1W', '1M', 'M', ...interval].includes(resolution);
};

export default {
  getBars: async (symbolInfo, resolution, from, to) => {
    if (to < 0) {
      return null;
    }

    if (isIndicator(symbolInfo)) {
      const intervalByIndicator =
        symbolInfo.name.includes(BUSDCode) ||
        symbolInfo.name.includes(ForeignTradingCode)
          ? ['1', '5', '15', '30', '60', '120', '240']
          : [];

      if (
        !isValidIndicatorInterval(symbolInfo, resolution, intervalByIndicator)
      ) {
        return null;
      }
      let data = [];
      if (symbolInfo.name.includes(BUSDCode)) {
        data = await getBUSDFromApi(
          symbolInfo,
          resolution,
          from,
          to,
          getIndicatorParams,
        );
      } else if (symbolInfo.name.includes(ForeignTradingCode)) {
        data = await getForeignTradingFromApi(
          symbolInfo,
          resolution,
          from,
          to,
          getIndicatorParams,
        );
      } else {
        const indicatorParams = getIndicatorParams(
          symbolInfo,
          resolution,
          from,
          to,
        );
        if (!indicatorParams) return null;
        data = await chartingService
          .getIndicator(indicatorParams)
          .then(({ data }) => data);
      }
      if (!data || data.items === null) {
        return null;
      }
      return data.items.map(formatBar(resolution));
    }

    const barParams = getBarParams(symbolInfo, resolution, from, to);
    if (!barParams) return null;
    const data = await chartingService
      .getTradingViewData(barParams)
      .then(data => data);

    if (!data || data.items === null) {
      return null;
    }
    return data.items.map(
      formatBar(
        resolution,
        symbolInfo.type === SYMBOL_TYPES.STOCK ||
          symbolInfo.type === SYMBOL_TYPES.COVERED_WARRANT,
      ),
    );
  },

  getTimescaleMarks: async (symbolInfo, from, to) => {
    const response = await chartingService.getStockEvents(
      getEventParam(symbolInfo, from, to),
    );
    if (!response.data || !Array.isArray(response.data.items)) {
      return [];
    }

    return response.data.items.reduce(formatTimescaleMarks, []);
  },

  getMarks: async (symbolInfo, from, to) => {
    const response = await chartingService.getStockEvents(
      getEventParam(symbolInfo, from, to),
    );
    if (!response.data || !Array.isArray(response.data.items)) {
      return [];
    }

    return response.data.items.reduce(formatMarks, []);
  },
};
